import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import { BaseTextFieldProps, InputBaseComponentProps, InputProps } from "@mui/material";
import { green } from "@mui/material/colors";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import { alpha, styled } from "@mui/material/styles";
import { useForkRef } from "@mui/material/utils";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import Text from "_shared/text/text";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import React, { forwardRef, JSX, useCallback, useEffect } from "react";
import { ProposalIssue } from "work/proposals/issues/proposal-issues";
import FieldUndoButton from "work/proposals/_views/_buttons/field-undo-button";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import ProposalFieldTimelineDropdown from "work/proposals/negotiation/proposal-field-timeline-dropdown";
import ProposalIncomingChangeHighlight from "work/proposals/negotiation/proposal-incoming-change-highlight";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import DiffText from "work/proposals/redlining/__diff/_views/diff-text";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";
import {
  RedlineCellActionSlot,
  redlineCellClasses,
} from "work/proposals/redlining/field-redlines/_views/redline-cell";
import {
  editableRedlineDisplayProps,
  ESCAPE_HINT,
  focusNextField,
  hasProposedChange,
  keepFocusOn,
  placeCaretAtEnd,
} from "work/proposals/redlining/field-redlines/_views/redline-field-focus";
import ResolutionTargetAuthorLabel from "work/proposals/redlining/field-redlines/_views/resolution-target-author-label";

/** The helper text of a required field left empty, while it keeps the focus. */
export const TEXT_REQUIRED_MESSAGE = `Enter a value, ${ESCAPE_HINT}.`;

const RedlineTextBox = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "traversable",
})<{ traversable?: boolean }>(() => ({
  minWidth: "20rem",
  "& .MuiTypography-root": {
    height: "unset",
  },
  width: "min-content",
  "& .Mui-disabled div span": {
    webkitTextFillColor: "revert",
    opacity: "0.75",
  },
}));
const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
const TextContainer = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "disableFullWidth" && prop !== "showTextInput" && prop !== "bold" && prop !== "cellLayout",
})<{ bold?: boolean; cellLayout?: boolean; disableFullWidth?: boolean; showTextInput: boolean; }>(
  ({ theme, bold, cellLayout, disableFullWidth, showTextInput }) => ({
    pointerEvents: showTextInput ? "none" : "auto",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    // Anchors the transient incoming-change tint to this field's value. Positioning alone changes no
    // geometry, so the field lays out exactly as it did before.
    position: "relative",
    paddingRight: theme.spacing(2),
    width: disableFullWidth ? "unset" : "100%",
    fontWeight: bold ? "500" : "normal",
    // In a table cell the value is the flexible part of the cell and the edit control's slot the fixed
    // part (see `redline-cell.tsx`): the value takes the room that is left and cuts off what does not
    // fit, and the slot keeps its own gap from it.
    ...(cellLayout && {
      flex: "1 1 auto",
      minWidth: 0,
      overflow: "hidden",
      paddingRight: 0,
      width: "auto",
      "& > .MuiInputBase-input": {
        minWidth: 0,
      },
    }),
  }),
);
const InputContainer = styled("span")(() => ({
  "&.MuiInputBase-input": {
    display: "flex",
    flexDirection: "column",
    height: "unset",
  },
}));
const ResolvedValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "singleLine",
})<{ singleLine?: boolean }>(({ singleLine }) => ({
  color: "inherit",
  fontWeight: "inherit",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  ...(singleLine && {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
}));
const InputField = styled(Input, {
  shouldForwardProp: (prop) => prop !== "show",
})<{ show: boolean }>(({ show, theme }) => ({
  height: show ? "unset" : 0,
  margin: theme.spacing(2, 0, 1),
  width: show ? "100%" : 0,
  overflow: show ? "visible" : "hidden",
  padding: 0,
  pointerEvents: show ? "auto" : "none",
  position: show ? "relative" : "absolute",
  "& .MuiInputBase-input": {
    padding: 0,
  },
}));

/** A cell-layout field fills its cell, whatever width the table gives the column. */
const cellLayoutFieldStyles = {
  margin: 0,
  minWidth: 0,
  width: "100%",
} as const;
/** The input row of a cell-layout field: the value, then the slot, across the whole cell. */
const cellLayoutInputStyles = {
  alignItems: "center",
  display: "flex",
  minWidth: 0,
  width: "100%",
} as const;

interface InputSubComponentProps extends InputBaseComponentProps {
  textRedline: FieldRedline<Text>;
  issues?: ProposalIssue[];
  hideAcceptRejectButtons: boolean;
  hideUndoButton: boolean;
  readOnly?: boolean;
  disableFullWidth?: boolean;
  /** See `RedlineTextInputProps.cellLayout`. */
  cellLayout?: boolean;
  hideFieldHistory?: boolean;
  /** See `RedlineTextInputProps.hideHistoryBadge`. */
  hideHistoryBadge?: boolean;
  /** See `RedlineTextInputProps.revertsFromHistory`. */
  revertsFromHistory?: boolean;
  /** See `RedlineTextInputProps.historyReadOnlyReason`. */
  historyReadOnlyReason?: string;
  negotiationId?: string;
  viewerScope?: string;
  focused: boolean;
  /** The field's name, which its display announces when it takes the focus. */
  fieldLabel?: string;
  /** See `RedlineTextInputProps.multiline`. */
  multilineValue?: boolean;
  showPlaceholder?: boolean;
  bold?: boolean;
  animateAcceptedResolved?: boolean;
  onAcceptedResolvedAnimationStart?: () => void;
  onTraverseIn?: () => void;
  fieldConstructor: new (value: string) => Text;
  onTextFieldRedlineChange: (newRedline: FieldRedline<Text>, traversalFieldOverride?: ProposalField | null) => void;
  onActionButtonsChange?: (actionButtons: JSX.Element) => void;
  /** Reports the error a value that cannot be committed has, and undefined once it has none. */
  onDraftErrorChange?: (error: string | undefined) => void;
  onShowTextInput?: () => void;
  onHideTextInput?: () => void;
}

const InputSubComponent = forwardRef<HTMLInputElement, InputSubComponentProps>(function InputSubComponent(
  props,
  forwardedRef
) {
  const {
    className,
    textRedline,
    issues,
    hideAcceptRejectButtons,
    hideUndoButton,
    hideFieldHistory,
    hideHistoryBadge,
    revertsFromHistory,
    historyReadOnlyReason,
    negotiationId,
    viewerScope,
    readOnly,
    disableFullWidth,
    cellLayout,
    focused,
    fieldLabel,
    multilineValue,
    required,
    bold,
    animateAcceptedResolved,
    onAcceptedResolvedAnimationStart,
    fieldConstructor,
    onTraverseIn,
    onTextFieldRedlineChange,
    onActionButtonsChange,
    onDraftErrorChange,
    onFocus,
    onBlur,
    onShowTextInput,
    onHideTextInput
  } = props as InputSubComponentProps;

  const getBaseValue = React.useCallback(() => {
    return textRedline.currentEntry?.value ?? textRedline.revisedEntry?.value ?? "";
  }, [textRedline]);

  const [draftTextValue, setDraftTextValue] = React.useState<string>(
    textRedline.currentEntry?.value ?? textRedline.revisedEntry?.value ?? ""
  );
  const [showTextInput, setShowTextInput] = React.useState<boolean>(false);
  // Set by Escape, which closes the input itself: the blur that follows must not commit what it reverted.
  const revertingRef = React.useRef(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const handleInputRef = useForkRef(forwardedRef, inputRef);

  // The input takes the focus the moment it opens, in place of the display that had it.
  useEffect(() => {
    if (showTextInput) inputRef.current?.focus();
  }, [showTextInput]);

  const acceptRejectButtons = React.useMemo(() => {
    if (hideAcceptRejectButtons || textRedline.isResolved) return null;

    return (
      <>
        <Tooltip title="Accept Change">
          <span>
            <ActionButton
              aria-label="Accept Change"
              onClick={() => {
                const newRedline = textRedline.accept();
                onAcceptedResolvedAnimationStart?.();
                onTextFieldRedlineChange(newRedline);
              }}
            >
              <CheckIcon color="success" />
            </ActionButton>
          </span>
        </Tooltip>
        <Tooltip title="Reject Change">
          <span>
            <ActionButton
              aria-label="Reject Change"
              onClick={() => {
                const newRedline = textRedline.reject();
                onTextFieldRedlineChange(newRedline);
              }}
            >
              <CloseIcon color="error" />
            </ActionButton>
          </span>
        </Tooltip>
      </>
    );
  }, [textRedline, onTextFieldRedlineChange, hideAcceptRejectButtons, onAcceptedResolvedAnimationStart]);

  const undoButton = React.useMemo(() => {
    if (hideUndoButton || !textRedline.canBeUndone) return null;

    return (
      <FieldUndoButton
        onUndo={() => {
          const newRedline = textRedline.undo();
          onTextFieldRedlineChange(newRedline, newRedline.field);
        }}
      />
    );
  }, [textRedline, onTextFieldRedlineChange, hideUndoButton]);

  const actionButtons = React.useMemo(() => {
    const buttons = (
      <>
        {!showTextInput && !readOnly && (
          <>
            {acceptRejectButtons}
            {undoButton}
          </>
        )}
        {!hideFieldHistory && revertsFromHistory && !readOnly && (
          <ProposalFieldTimelineDropdown
            field={textRedline.field}
            // The name the read-only control has, so the field's history reads the same either way.
            toolTip="View change history"
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            hideBadge={hideHistoryBadge}
            // An earlier value goes back through the same edit a typed one does, so it is a proposed
            // change like any other: shown in the redline, undoable, and sent on the next save.
            onRevert={(entry) => {
              if (entry.nextValue === undefined) return;
              onTextFieldRedlineChange(textRedline.edit(new fieldConstructor(String(entry.nextValue))), textRedline.field);
            }}
          />
        )}
        {!hideFieldHistory && !(revertsFromHistory && !readOnly) && (
          <ProposalFieldHistoryButton
            field={textRedline.field}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            hideBadge={hideHistoryBadge}
            readOnlyReason={historyReadOnlyReason}
          />
        )}
      </>
    );
    // A read-only cell with no history control has nothing to put in the slot, so it draws none.
    if (cellLayout && !(readOnly && hideFieldHistory)) {
      return <RedlineCellActionSlot>{buttons}</RedlineCellActionSlot>;
    }
    return buttons;
  }, [
    cellLayout,
    showTextInput,
    readOnly,
    textRedline,
    hideFieldHistory,
    hideHistoryBadge,
    revertsFromHistory,
    historyReadOnlyReason,
    negotiationId,
    viewerScope,
    fieldConstructor,
    onTextFieldRedlineChange,
    acceptRejectButtons,
    undoButton,
  ]);

  useEffect(() => {
    onActionButtonsChange?.(actionButtons);
  }, [onActionButtonsChange, actionButtons]);

  const handleOpenTextInput = useCallback(() => {
    if (readOnly) return;
    revertingRef.current = false;
    setDraftTextValue(getBaseValue());
    setShowTextInput(true);
    onTraverseIn?.();
    onShowTextInput?.();
  }, [getBaseValue, onShowTextInput, onTraverseIn, readOnly]);

  function closeTextInput() {
    setShowTextInput(false);
    onDraftErrorChange?.(undefined);
    onHideTextInput?.();
  }

  function draftErrorFor(value: string): string | undefined {
    return required && !value.trim() ? TEXT_REQUIRED_MESSAGE : undefined;
  }

  function commitDraft() {
    if (draftTextValue !== getBaseValue()) {
      try {
        onTextFieldRedlineChange(textRedline.edit(new fieldConstructor(draftTextValue)), textRedline.field);
      } catch (error) {
        console.error("Error updating text", error);
        showErrorSnackbar(error, { doing: "updating the text", operation: "proposal.redline.edit" });
      }
    }
    closeTextInput();
  }

  // Leaving the field commits it, as the ✓ beside it used to. A value that cannot be committed keeps
  // the focus instead, with its error, until it is one that can or Escape reverts it.
  function handleTextInputBlur(event: React.FocusEvent<HTMLInputElement>) {
    onBlur?.(event);
    if (revertingRef.current) return;
    const error = draftErrorFor(draftTextValue);
    if (error) {
      onDraftErrorChange?.(error);
      keepFocusOn(event.currentTarget);
      return;
    }
    commitDraft();
  }

  function handleTextInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === KeyboardKey.Escape) {
      // The field's own Escape, not the drawer's or the dialog's it sits in.
      event.stopPropagation();
      revertingRef.current = true;
      setDraftTextValue(getBaseValue());
      closeTextInput();
      event.currentTarget.blur();
      return;
    }
    // A description keeps Enter for its new lines; a one-line field commits and moves on.
    if (event.key !== KeyboardKey.Enter || multilineValue || event.shiftKey) return;
    event.preventDefault();
    const error = draftErrorFor(draftTextValue);
    if (error) {
      onDraftErrorChange?.(error);
      return;
    }
    focusNextField(event.currentTarget);
  }

  function handleDisplayFocus(event: React.FocusEvent<HTMLElement>) {
    onFocus?.(event as React.FocusEvent<HTMLInputElement>);
    handleOpenTextInput();
  }

  const resolvedTextChanges = React.useMemo(() => {
    if (!textRedline.isResolved || !textRedline.textChanges?.length) return undefined;
    return textRedline.textChanges;
  }, [textRedline]);
  const showAcceptedResolvedAnimation = Boolean(
    animateAcceptedResolved &&
    textRedline.isResolved &&
    textRedline.isAccepted &&
    textRedline.wasRevised &&
    textRedline.currentEntry !== null &&
    resolvedTextChanges
  );
  const showResolvedDiff = Boolean(
    resolvedTextChanges &&
    (!textRedline.isAccepted || !textRedline.wasRevised || showAcceptedResolvedAnimation || textRedline.currentEntry === null)
  );
  const showResolvedCurrentValue = Boolean(
    textRedline.isResolved &&
    textRedline.currentEntry &&
    (!resolvedTextChanges || (textRedline.isAccepted && textRedline.wasRevised && !showAcceptedResolvedAnimation))
  );

  return (
    <TextContainer
      className={cellLayout ? redlineCellClasses.value : undefined}
      bold={bold}
      cellLayout={cellLayout}
      disableFullWidth={disableFullWidth}
      showTextInput={showTextInput}
    >
      <ProposalIncomingChangeHighlight field={textRedline.field} negotiationId={negotiationId} />
      <InputContainer
        className={className}
        // The redline is the field's display, and it takes the focus the way an input does: focusing it
        // opens the input in its place (#6624). A read-only field has nothing to open.
        ref={showTextInput || readOnly ? undefined : (forwardedRef as React.Ref<HTMLSpanElement>)}
        {...(!showTextInput && !readOnly &&
          editableRedlineDisplayProps(fieldLabel, hasProposedChange(textRedline.textChanges)))}
        onFocus={showTextInput || readOnly ? undefined : handleDisplayFocus}
      >
        {!showTextInput && !textRedline.isResolved && textRedline.incomingAuthor && (
          <ResolutionTargetAuthorLabel author={textRedline.incomingAuthor} />
        )}
        {/* An SVG dash, not `<Icon>remove</Icon>`: that is a font ligature, the word "remove" until the
            icon font from Google Fonts applies, and its 1em clip showed "re" where it had not (#6572).
            In a cell it is the size the read-only fee schedule table's dash is. */}
        {!showTextInput && !textRedline.textChanges && (
          <RemoveIcon fontSize={cellLayout ? "small" : "medium"} />
        )}
        {!showTextInput && showResolvedDiff && resolvedTextChanges && (
          <DiffText
            singleLine={cellLayout}
            textChanges={resolvedTextChanges}
            allResolved={true}
            acceptedResolved={textRedline.isAccepted && textRedline.wasRevised}
            animateAcceptedResolved={animateAcceptedResolved}
          />
        )}
        {!showTextInput && showResolvedCurrentValue && (
          <ResolvedValue
            singleLine={cellLayout}
          >
            {textRedline.currentEntry?.value}
          </ResolvedValue>
        )}
        {!showTextInput && !textRedline.isResolved && textRedline.textChanges && (
          <DiffText
            singleLine={cellLayout}
            textChanges={textRedline.textChanges}
            allResolved={textRedline.isResolved}
          />
        )}
        {showTextInput && (
          <>
            {/* No "previously:" / "your changes:" preview above the input: the field's own history
                control carries its history, and repeating it beside every editor spent the room the
                editor itself needed and made one text field look unlike the next. */}
            <FormControl variant="standard" focused={focused}>
              <InputField
                multiline
                inputRef={handleInputRef}
                show={true}
                placeholder="Enter new text"
                error={issues && issues.length > 0}
                value={draftTextValue}
                onChange={(event) => {
                  setDraftTextValue(event.target.value);
                  if (!draftErrorFor(event.target.value)) onDraftErrorChange?.(undefined);
                }}
                onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                  placeCaretAtEnd(event.currentTarget);
                  onFocus?.(event);
                }}
                onBlur={handleTextInputBlur}
                onKeyDown={handleTextInputKeyDown}
              />
            </FormControl>
          </>
        )}
      </InputContainer>
    </TextContainer>
  );
});

export interface RedlineTextInputProps<T extends Text> extends BaseTextFieldProps {
  textRedline: FieldRedline<T>;
  issues?: ProposalIssue[];
  showTextInput?: boolean;
  showResolvedHelperText?: boolean;
  readOnly?: boolean;
  hideUndoButton?: boolean;
  hideAcceptRejectButtons?: boolean;
  hideLabel?: boolean;
  hideFieldHistory?: boolean;
  /**
   * Suppresses the embedded `ProposalFieldHistoryButton`'s own change-badge bubbles. Set by a caller
   * that already renders a `ProposalFieldChangeBadge` on this field's label/wrapper, so the same
   * unread/collision signal is never shown twice — see `ProposalFieldHistoryButtonProps.hideBadge`.
   */
  hideHistoryBadge?: boolean;
  /**
   * Swaps the read-only history popover for the revert menu while the field is editable (#6874), so an
   * earlier value can be picked to put it back, as the draft page's Details fields always could.
   * Opt-in, because only a field whose value is one piece of text reverts through `textRedline.edit`;
   * a cell in a collection table reverts through its collection's operations instead.
   */
  revertsFromHistory?: boolean;
  /**
   * What the read-only history popover says about why its values cannot be picked. See
   * `ProposalFieldHistoryButtonProps.readOnlyReason`.
   */
  historyReadOnlyReason?: string;
  lockAdornment?: React.ReactNode;
  negotiationId?: string;
  viewerScope?: string;
  showPlaceholder?: boolean;
  bold?: boolean;
  disableFullWidth?: boolean;
  /**
   * Lays the field out as one cell of a redline table (#5405): it fills the cell, draws its value on
   * one line cut off with an ellipsis, and puts the edit control in the fixed-width slot at the cell's
   * trailing edge, at the size every other editable cell draws it — see `redline-cell.tsx`. Without it
   * the field keeps the form layout the details tab uses.
   */
  cellLayout?: boolean;
  /**
   * The value runs over several lines, so Enter adds a line rather than committing and moving to the
   * next field, as it does in a one-line field (#6624). The input wraps a long value either way.
   */
  multiline?: boolean;
  onTraverseIn?: () => void;
  fieldConstructor: new (value: string) => T;
  onTextFieldRedlineChange: (newRedline: FieldRedline<Text>, traversalFieldOverride?: ProposalField | null) => void;
  /**
   * Reports the error the field's input holds while its value cannot be committed (a required field
   * left empty), and undefined once it can or Escape has put back the last value. Nothing about that
   * value has reached `onTextFieldRedlineChange`, so a form must not save while it is set.
   */
  onDraftErrorChange?: (error: string | undefined) => void;
  onShowTextInput?: () => void;
  onHideTextInput?: () => void;
}

const TextRedlineInput = <T extends Text>(props: Readonly<RedlineTextInputProps<T>>) => {
  const {
    disabled,
    textRedline,
    issues,
    showResolvedHelperText: showResolvedHelperTextProp,
    hideUndoButton,
    hideAcceptRejectButtons,
    readOnly,
    hideLabel,
    label,
    hideFieldHistory,
    hideHistoryBadge,
    revertsFromHistory,
    historyReadOnlyReason,
    lockAdornment,
    variant,
    margin,
    negotiationId,
    viewerScope,
    showPlaceholder,
    bold,
    disableFullWidth,
    cellLayout,
    multiline,
    focused,
    fieldConstructor,
    onTextFieldRedlineChange,
    onDraftErrorChange,
    onTraverseIn,
    onFocus,
    onBlur,
    onShowTextInput,
    onHideTextInput,
    ...otherProps
  } = props;
  const showResolvedHelperText = showResolvedHelperTextProp ?? true;

  const [actionButtons, setActionButtons] = React.useState<JSX.Element>();
  const hasAcceptedResolvedChange = textRedline.isResolved && textRedline.isAccepted && textRedline.wasRevised;
  const [animateAcceptedResolved, setAnimateAcceptedResolved] = React.useState(false);
  const [draftError, setDraftError] = React.useState<string>();
  const issuesText = issues?.map((entry) => entry.description).join("\n");
  const helperText = draftError ?? (hasAcceptedResolvedChange && showResolvedHelperText ? "Resolved" : issuesText);
  // The field's name for its display to announce: the label it draws, or the redline's own when the
  // caller draws none or draws something other than text.
  const fieldLabel = typeof label === "string" ? label : textRedline.label;
  const acceptedResolvedAnimationTimeoutRef = React.useRef<ReturnType<typeof globalThis.setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (acceptedResolvedAnimationTimeoutRef.current !== undefined) {
        globalThis.clearTimeout(acceptedResolvedAnimationTimeoutRef.current);
      }
    };
  }, []);

  const triggerAcceptedResolvedAnimation = useCallback(() => {
    setAnimateAcceptedResolved(true);
    if (acceptedResolvedAnimationTimeoutRef.current !== undefined) {
      globalThis.clearTimeout(acceptedResolvedAnimationTimeoutRef.current);
    }
    acceptedResolvedAnimationTimeoutRef.current = globalThis.setTimeout(() => {
      setAnimateAcceptedResolved(false);
      acceptedResolvedAnimationTimeoutRef.current = undefined;
    }, 1000);
  }, []);

  const handleDraftErrorChange = useCallback((error: string | undefined) => {
    setDraftError(error);
    onDraftErrorChange?.(error);
  }, [onDraftErrorChange]);

  const handleActionButtonsChange = useCallback((actionButtons: JSX.Element) => {
    setActionButtons(prev => (prev === actionButtons ? prev : actionButtons));
  }, []);

  const inputProps = React.useMemo<Partial<InputProps>>(() => {
    const baseProps: Partial<InputProps> = {
      inputComponent: InputSubComponent,
      inputProps: {
        readOnly,
        hideUndoButton,
        hideAcceptRejectButtons,
        textRedline,
        issues,
        hideFieldHistory,
        hideHistoryBadge,
        revertsFromHistory,
        historyReadOnlyReason,
        negotiationId,
        viewerScope,
        focused,
        showPlaceholder,
        bold,
        disableFullWidth,
        cellLayout,
        fieldLabel,
        multilineValue: multiline,
        onTraverseIn,
        onTextFieldRedlineChange,
        fieldConstructor,
        onActionButtonsChange: handleActionButtonsChange,
        onDraftErrorChange: handleDraftErrorChange,
        onFocus,
        onBlur,
        onShowTextInput,
        onHideTextInput,
        animateAcceptedResolved,
        onAcceptedResolvedAnimationStart: triggerAcceptedResolvedAnimation,
      },
      endAdornment: actionButtons,
      startAdornment: lockAdornment,
      ...(cellLayout && { sx: cellLayoutInputStyles }),
    };
    return (variant && variant !== "outlined")
      ? { ...baseProps, disableUnderline: true }
      : baseProps;
  }, [
    readOnly,
      hideUndoButton,
      hideAcceptRejectButtons,
    textRedline,
    issues,
    hideFieldHistory,
    hideHistoryBadge,
    revertsFromHistory,
    historyReadOnlyReason,
    lockAdornment,
    negotiationId,
    viewerScope,
    focused,
    showPlaceholder,
    bold,
    disableFullWidth,
    cellLayout,
    fieldLabel,
    multiline,
    onTraverseIn,
    onTextFieldRedlineChange,
    fieldConstructor,
    onFocus,
    onBlur,
    onShowTextInput,
    onHideTextInput,
    animateAcceptedResolved,
    actionButtons,
    variant,
    handleActionButtonsChange,
    handleDraftErrorChange,
    triggerAcceptedResolvedAnimation,
  ]);

  return (
    <RedlineTextBox
      {...otherProps}
      disabled={disabled}
      focused={focused}
      traversable={Boolean(onTraverseIn)}
      error={Boolean(issues?.length) || Boolean(draftError)}
      helperText={helperText}
      // A caller's own label wins over the redline's, which names the field the redline belongs to —
      // for a category's columns that is "Fee Schedule", whichever column it is.
      label={hideLabel ? undefined : (label ?? textRedline.label)}
      variant={variant ?? "outlined"}
      margin={margin ?? "normal"}
      sx={{
        ...(cellLayout && cellLayoutFieldStyles),
        ...(hasAcceptedResolvedChange && {
          "& .MuiInputLabel-root": {
            color: green[700],
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: green[700],
          },
          "& .MuiOutlinedInput-root": {
            backgroundColor: alpha(green[500], 0.06),
          },
          "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: green[500],
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: green[600],
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: green[700],
          },
          "& .MuiFormHelperText-root": {
            color: green[700],
          },
        }),
        ...(lockAdornment && {
          "& .MuiInputLabel-root.MuiInputLabel-shrink": {
            color: "warning.main",
          },
          "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-disabled": {
            color: "warning.main",
          },
          "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-focused": {
            color: "warning.main",
          },
          "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: "warning.main",
          },
          "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "warning.main",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "warning.main",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "warning.main",
          },
        }),
      }}
      slotProps={{
        input: inputProps,
        inputLabel: { shrink: true },
      }}
      onFocus={onTraverseIn}
    />
  );
};

export default TextRedlineInput;
